import { TanStackDevtools } from '@tanstack/react-devtools'
import { createRootRoute, Outlet, useMatches } from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { Toaster } from '@wedding-tech/ui/sonner'
import type { ReactNode } from 'react'
import z from 'zod'

import { TooltipProvider } from '@/components/ui/tooltip'
import { LIFFProvider } from '@/lib/liff/provider'
import { useLiffId } from '@/lib/liff/useLiffId'
import { QueryProvider } from '@/lib/query'
import { useVConsole } from '@/lib/vconsole'

const RootSearchSchema = z.object({ liffId: z.string().optional() })

function RootLIFFWrapper({ children }: { children: ReactNode }) {
  const matches = useMatches()

  const { liffId: activeLiffId } = useLiffId()

  // Compute withLogin from active route staticData

  const authRequired = !matches.some(
    (match) => match.staticData?.authRequired === false,
  )

  return (
    <LIFFProvider
      liffId={activeLiffId}
      withLogin={authRequired}
    >
      {children}
    </LIFFProvider>
  )
}

const RootLayout = () => {
  useVConsole()

  return (
    <TooltipProvider>
      <QueryProvider>
        <RootLIFFWrapper>
          <Outlet />
        </RootLIFFWrapper>

        <TanStackDevtools
          config={{
            hideUntilHover: true,
            inspectHotkey: ['CtrlOrMeta', 'Shift'],
          }}
          plugins={[
            {
              name: 'Tanstack Router',
              render: <TanStackRouterDevtoolsPanel />,
            },
          ]}
        />

        <Toaster toastOptions={{ className: 'font-sans' }} />
      </QueryProvider>
    </TooltipProvider>
  )
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4 text-center">
      <h1 className="text-xl font-semibold text-neutral-800 dark:text-neutral-200">
        404 - Page Not Found
      </h1>
      <p className="mt-1 text-sm text-neutral-500">
        The requested page could not be found.
      </p>
    </div>
  )
}

export const Route = createRootRoute({
  component: RootLayout,
  validateSearch: RootSearchSchema,
  notFoundComponent: NotFoundComponent,
})
